<script setup>
import { useRoute } from 'vue-router';

import { storeToRefs } from 'pinia';

import { X, Megaphone, Store, BookOpen } from 'lucide-vue-next';

import Select from 'primevue/select';

import { businessProfileHasBook } from '@/helpers/marketplaceAccountType.js';
import { useAccountStore } from '@/stores/account.js';
import { useAuthStore } from '@/stores/auth.js';

const route = useRoute();
const account = useAccountStore();

const hideProfileSelect = computed(() =>
  ['register-verify', 'register-success', 'create-business', 'connect-accounts'].includes(route.name),
);
const selectRef = ref(null);
const filterValue = ref('');
const authStore = useAuthStore();
const { changeBusinessProfile, ensureCurrentProfileLoaded } = account;
const { businessProfiles, currentBusinessProfile } = storeToRefs(account);
const { isInitializingAuth } = storeToRefs(authStore);

const hasAdsConnected = (profile) => {
  return profile.amazonAdsAccounts?.some((acc) => acc.amazonAdsProfiles?.length > 0) ?? false;
};

const hasSpConnected = (profile) => {
  return profile.amazonSPAccounts?.some((acc) => acc.marketplaces?.length > 0) ?? false;
};

const sortedBusinessProfiles = computed(() => {
  if (!businessProfiles.value) {
    return [];
  }
  // Enrich list with full data from currentBusinessProfile (which has connection details)
  const current = currentBusinessProfile.value;
  return [...businessProfiles.value]
    .map((p) => (current && p.id === current.id ? { ...p, ...current } : p))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
});

// Initialize profile on mount - wait for auth initialization first
onMounted(async () => {
  // If auth is initializing, wait for it to complete
  if (isInitializingAuth.value) {
    await new Promise((resolve) => {
      const unwatch = watch(isInitializingAuth, (val) => {
        if (!val) {
          unwatch();
          resolve();
        }
      }, { immediate: true });
    });
  }

  ensureCurrentProfileLoaded();
});

const onFilterInput = (event) => {
  filterValue.value = event.value ?? '';
};

const clearFilter = () => {
  if (selectRef.value) {
    selectRef.value.filterValue = '';
    filterValue.value = '';
    // Trigger the component to re-filter with empty value
    const filterInput = selectRef.value.$el?.querySelector('.p-select-filter');
    if (filterInput) {
      filterInput.value = '';
      filterInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
};

// Handler for profile change - calls store directly
const onProfileChange = async (newProfile) => {
  if (newProfile?.id && newProfile.id !== currentBusinessProfile.value?.id) {
    await changeBusinessProfile(newProfile);
  }
};
</script>

<template>
  <Select
    v-if="!hideProfileSelect"
    id="olap-bp-select"
    ref="selectRef"
    :model-value="currentBusinessProfile"
    :options="sortedBusinessProfiles"
    option-label="name"
    data-key="id"
    :filter="sortedBusinessProfiles.length > 5"
    :auto-filter-focus="sortedBusinessProfiles.length > 5"
    :filter-placeholder="$t('Search')"
    size="large"
    class="header-account"
    @update:model-value="onProfileChange"
    @filter="onFilterInput"
  >
    <template #filtericon>
      <span
        v-if="filterValue"
        class="filter-clear-icon"
        @click.stop="clearFilter"
      >
        <X :size="16" />
      </span>
      <span v-else class="pi pi-search" />
    </template>
    <template #value="{ value, placeholder }">
      <span class="header-icon header-account-icon">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M18 6H16C16 3.79 14.21 2 12 2C9.79 2 8 3.79 8 6H6C4.9 6 4 6.9 4 8V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8C20 6.9 19.1 6 18 6ZM10 10C10 10.55 9.55 11 9 11C8.45 11 8 10.55 8 10V8H10V10ZM12 4C13.1 4 14 4.9 14 6H10C10 4.9 10.9 4 12 4ZM16 10C16 10.55 15.55 11 15 11C14.45 11 14 10.55 14 10V8H16V10Z"
            fill="var(--text-main-color)"
          />
        </svg>
      </span>
      <span
        v-if="value"
        v-no-translate
        class="header-account-seller-account-name"
      >
        {{ value.name }}
      </span>
      <span v-else>
        {{ placeholder }}
      </span>
    </template>
    <template #option="{ option }">
      <div class="header-account-option">
        <div class="header-account-option-icons">
          <Megaphone
            :size="14"
            class="header-account-option-icon"
            :class="{ 'header-account-option-icon--active': hasAdsConnected(option) }"
            :title="hasAdsConnected(option) ? $t('Advertising account connected') : $t('Advertising account not connected')"
          />
          <component
            :is="businessProfileHasBook(option) ? BookOpen : Store"
            :size="14"
            class="header-account-option-icon"
            :class="{ 'header-account-option-icon--active': hasSpConnected(option) }"
            :title="hasSpConnected(option) ? $t('Seller Partner connected') : $t('Seller Partner not connected')"
          />
        </div>
        <span v-no-translate class="header-account-option-name">{{ option.name }}</span>
      </div>
    </template>
  </Select>
</template>

<style lang="scss" scoped>
.header-menu {
  .p-select-label {
    padding-right: 8px;
  }
}
.header-account {
  margin: 0;
  width: auto;
  min-width: 180px;
  padding-right: 12px;

  @media (max-width: 768px) {
    min-width: 140px;
    padding-right: 8px;
    border-radius: 14px;

    &:deep(.p-select-label) {
      padding: 2px 4px;
    }

    &:deep(.p-select-dropdown) {
      width: 2rem;
    }
  }
}

.filter-clear-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-tertiary-text-color));
  transition: color 0.2s;

  &:hover {
    color: rgb(var(--v-theme-text-main-color));
  }
}
</style>

<style lang="scss">
.header-account-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.header-account-option-icons {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.header-account-option-icon {
  color: var(--wp-text-disabled, #999);
  opacity: 0.4;
  transition: all 0.2s ease;

  &--active {
    color: var(--wp-icon-main-to-white, #7c4dff);
    opacity: 1;
  }
}

.header-account-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
